import { useCallback, useEffect, useState } from "react"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"

import { defaultCoordinates } from "@/lib/config"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area"
import { Icons } from "@/components/icons"
import {
  getAddressSuggestionsAction,
  getPlaceDetailsAction,
} from "@/app/actions/address"
import { updateGeocodeCookie } from "@/app/actions/geocode"
import { checkServiceAvailableAction } from "@/app/actions/location"

import { useLocation } from "./hooks"
import { Address } from "./store"
import { useLocationDialogStore } from "./store/dialog"

const LocationSelector = () => {
  const {
    address,
    isLoading,
    getCurrentPosition,
    setUserAddress,
    coordinates,
  } = useLocation()

  const { isOpen, setIsOpen } = useLocationDialogStore()
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false)

  // Address autocomplete state
  const [searchText, setSearchText] = useState("")
  const [debouncedSearchText, setDebouncedSearchText] = useState("")
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([])
  const [isSearching, setIsSearching] = useState(false)

  // Debounce search text input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchText])

  // Fetch address suggestions when debounced search text changes
  useEffect(() => {
    const fetchAddressSuggestions = async () => {
      if (debouncedSearchText.trim().length < 2) {
        setAddressSuggestions([])
        return
      }

      setIsSearching(true)
      try {
        const result = await getAddressSuggestionsAction({
          textInput: debouncedSearchText,
          // Use current coordinates if available
          ...(coordinates && {
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
          }),
        })

        if (result.predictions && result.predictions.length > 0) {
          setAddressSuggestions(result.predictions)
        } else {
          setAddressSuggestions([])
        }
      } catch (error) {
        console.error("Error fetching address suggestions:", error)
        setAddressSuggestions([])
      } finally {
        setIsSearching(false)
      }
    }

    if (debouncedSearchText) {
      fetchAddressSuggestions()
    }
  }, [debouncedSearchText, coordinates])

  const handleGetLocation = async () => {
    setTooltipOpen(false)
    await getCurrentPosition()
  }

  const handleSelectAddress = useCallback(
    async (suggestion: any) => {
      try {
        if (suggestion.place_id) {
          const placeDetails = await getPlaceDetailsAction({
            placeId: suggestion.place_id,
            ...(coordinates && {
              latitude: coordinates.latitude,
              longitude: coordinates.longitude,
            }),
          })

          if (placeDetails?.result) {
            // Create the final address with coordinates
            const finalAddress: Address = {
              full: placeDetails.result.formatted_address,
              display:
                placeDetails.result.name ||
                suggestion.structured_formatting.main_text,
              placeId: suggestion.place_id,
              name: placeDetails.result.name,
              coordinates: {
                latitude: placeDetails.result.geometry.location.lat,
                longitude: placeDetails.result.geometry.location.lng,
              },
            }

            const { available } = await checkServiceAvailableAction(
              finalAddress.coordinates?.latitude || defaultCoordinates.latitude,
              finalAddress.coordinates?.longitude ||
                defaultCoordinates.longitude
            )

            if (!available) {
              toast.error(
                "Hiện tại beFood chưa có mặt tại khu vực của bạn. Vui lòng chọn địa chỉ mới hoặc thử lại sau nhé."
              )
              return
            }

            // First, create a temporary address from the suggestion
            const tempAddress: Address = {
              full: suggestion.description,
              display: suggestion.structured_formatting.main_text,
              placeId: suggestion.place_id,
            }

            // Set the temporary address in the store
            setUserAddress(tempAddress)

            // Close dialogs
            setIsOpen(false)
            setIsAddressSearchOpen(false)
            setSearchText("")
            setAddressSuggestions([])

            // Update the address in the store with complete information
            setUserAddress(finalAddress)
            // Update geocode in the cookie
            updateGeocodeCookie(
              finalAddress.coordinates?.latitude || defaultCoordinates.latitude,
              finalAddress.coordinates?.longitude ||
                defaultCoordinates.longitude
            )
          }
        }
      } catch (error) {
        console.error("Error fetching place details:", error)
      }
    },
    [setUserAddress, coordinates, setIsOpen]
  )

  // Handle dialog close
  const handleDialogClose = () => {
    setIsOpen(false)
    setIsAddressSearchOpen(false)
    setSearchText("")
    setAddressSuggestions([])
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <button
            className={cn(
              "items-center flex-[0_1_auto] overflow-hidden hidden md:flex",
              "hover:bg-gray-100 px-2 py-1 rounded-lg transition-colors"
            )}
            onClick={() => setIsOpen(true)}
          >
            <div className="flex items-center gap-1 shrink-0 h-9">
              <div className="flex items-center gap-1">
                {isLoading ? (
                  <Loader2 className="animate-spin" size={12} />
                ) : (
                  <Icons.mapPin className="inline size-4 text-gray-600" />
                )}
                <span className="text-gray-950 text-sm font-medium truncate max-w-[150px]">
                  {address?.display || "Thay đổi địa chỉ"}
                </span>
              </div>
            </div>
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-[95vw] md:min-w-[600px] rounded-2xl">
          <DialogHeader className="mt-6">
            <DialogTitle className="md:text-3xl font-semibold text-left text-lg">
              Thông tin giao hàng
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4 mt-6 pb-2 px-3">
            <div className="flex gap-5 items-center text-lg">
              <Icons.mapPin
                className="text-gray-800"
                size={20}
                strokeWidth={2}
              />

              <div className="flex flex-col gap-1">
                <span className="font-semibold text-gray-950 uppercase text-xs">
                  Địa chỉ
                </span>
                <span className="text-gray-600 text-sm">
                  {address?.display || "Vui lòng thay đổi địa chỉ giao hàng"}
                </span>
              </div>

              <DialogClose className="ml-auto" asChild>
                <Button
                  className="rounded-3xl text-sm px-5 h-8 font-semibold bg-gray-200/60"
                  variant="secondary"
                  onClick={() => {
                    setIsAddressSearchOpen(true)
                  }}
                >
                  Thay đổi
                </Button>
              </DialogClose>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isAddressSearchOpen} onOpenChange={setIsAddressSearchOpen}>
        <DialogContent className="max-w-[95vw] md:min-w-[300px] rounded-2xl">
          <DialogHeader className="mt-6">
            <DialogTitle className="md:text-3xl font-semibold text-lg text-left">
              Giao tới
            </DialogTitle>
          </DialogHeader>

          <div className="mt-3">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Icons.mapPin
                  size={20}
                  strokeWidth={2}
                  className="absolute left-4 top-1/2 -translate-y-1/2"
                />

                <Input
                  placeholder="Nhập địa chỉ giao hàng"
                  className="h-12 rounded-xl pr-8 bg-gray-200/30 pl-12 placeholder:text-gray-600 placeholder:font-medium font-medium focus-visible:ring-blue-200 focus-visible:border-blue-500 focus-visible:text-gray-950"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                {isSearching && (
                  <Loader2
                    className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400"
                    size={16}
                  />
                )}
              </div>
            </div>

            {addressSuggestions.length > 0 && (
              <div className="mt-4">
                <ScrollArea className="h-[400px] max-h-[400px]">
                  <div className="py-2">
                    {addressSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.place_id}
                        className="w-full px-2 py-3 text-left rounded-md hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-0 flex gap-3 items-center"
                        onClick={async () => {
                          handleSelectAddress(suggestion)
                        }}
                      >
                        <Icons.mapPin
                          size={20}
                          strokeWidth={1}
                          className="shrink-0 text-gray-700"
                        />
                        <div className="flex flex-col">
                          <div className="font-medium text-gray-900 line-clamp-1 text-sm">
                            {suggestion.structured_formatting.main_text}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {suggestion.structured_formatting.secondary_text}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <ScrollBar orientation="vertical" />
                </ScrollArea>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export { LocationSelector }

// Re-export hooks for external use
export { useLocation, useCoordinatesParam, useAutoLocation } from "./hooks"

// Re-export types for external use
export type { Address, Coordinates } from "./types"
